/**
 * Endpoints de **Autoservicio → Dashboard** (`/api/autoservicio/dashboard/*`).
 *
 * Todos son de solo lectura y los calcula el servicio de autoservicio; esta API
 * sólo pone el secreto y la sesión del panel. Se consulta la pestaña visible:
 * cambiar de pestaña dispara su propia consulta (y así el detalle no se pide con
 * un `limite` que el usuario aún no eligió).
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  DetalleAutoservicio,
  FiltrosAutoservicio,
  OutboxAutoservicio,
  PorTurnoAutoservicio,
  ResumenAutoservicio,
} from './autoservicio.types';

export const autoservicioKeys = {
  resumen: (filtros: FiltrosAutoservicio) => ['autoservicio', 'resumen', filtros] as const,
  turnos: (filtros: FiltrosAutoservicio) => ['autoservicio', 'por-turno', filtros] as const,
  detalle: (filtros: FiltrosAutoservicio) => ['autoservicio', 'detalle', filtros] as const,
  outbox: (filtros: FiltrosAutoservicio) => ['autoservicio', 'outbox', filtros] as const,
};

/**
 * Cadena de consulta con los filtros del contrato.
 *
 * `turnoId` no se envía al outbox (el contrato lo ignora) y `limite` sólo al
 * detalle.
 */
function cadenaDe(
  filtros: FiltrosAutoservicio,
  { conTurno = true, conLimite = false } = {},
): string {
  const parametros = new URLSearchParams();

  if (filtros.desde) parametros.set('desde', filtros.desde);
  if (filtros.hasta) parametros.set('hasta', filtros.hasta);
  if (conTurno && filtros.turnoId) parametros.set('turnoId', filtros.turnoId);
  if (conLimite) parametros.set('limite', String(filtros.limite));

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const autoservicioApi = {
  resumen: (filtros: FiltrosAutoservicio, signal?: AbortSignal): Promise<ResumenAutoservicio> =>
    apiClient.get<ResumenAutoservicio>(`/api/autoservicio/dashboard/resumen${cadenaDe(filtros)}`, {
      signal,
    }),

  porTurno: (filtros: FiltrosAutoservicio, signal?: AbortSignal): Promise<PorTurnoAutoservicio> =>
    apiClient.get<PorTurnoAutoservicio>(
      `/api/autoservicio/dashboard/por-turno${cadenaDe(filtros)}`,
      { signal },
    ),

  detalle: (filtros: FiltrosAutoservicio, signal?: AbortSignal): Promise<DetalleAutoservicio> =>
    apiClient.get<DetalleAutoservicio>(
      `/api/autoservicio/dashboard/detalle${cadenaDe(filtros, { conLimite: true })}`,
      { signal },
    ),

  outbox: (filtros: FiltrosAutoservicio, signal?: AbortSignal): Promise<OutboxAutoservicio> =>
    apiClient.get<OutboxAutoservicio>(
      `/api/autoservicio/dashboard/outbox${cadenaDe(filtros, { conTurno: false })}`,
      { signal },
    ),
};

/** KPIs del período (solicitudes, documentación, comprobantes, saldos y outbox). */
export function useResumenAutoservicio(filtros: FiltrosAutoservicio, activo: boolean) {
  return useQuery({
    queryKey: autoservicioKeys.resumen(filtros),
    queryFn: ({ signal }) => autoservicioApi.resumen(filtros, signal),
    enabled: activo,
    placeholderData: keepPreviousData,
  });
}

/** Agregados por turno. */
export function usePorTurnoAutoservicio(filtros: FiltrosAutoservicio, activo: boolean) {
  return useQuery({
    queryKey: autoservicioKeys.turnos(filtros),
    queryFn: ({ signal }) => autoservicioApi.porTurno(filtros, signal),
    enabled: activo,
    placeholderData: keepPreviousData,
  });
}

/** Detalle fila por operación. */
export function useDetalleAutoservicio(filtros: FiltrosAutoservicio, activo: boolean) {
  return useQuery({
    queryKey: autoservicioKeys.detalle(filtros),
    queryFn: ({ signal }) => autoservicioApi.detalle(filtros, signal),
    enabled: activo,
    placeholderData: keepPreviousData,
  });
}

/** Salud de la cola de reintentos de documentación. */
export function useOutboxAutoservicio(filtros: FiltrosAutoservicio, activo: boolean) {
  return useQuery({
    queryKey: autoservicioKeys.outbox(filtros),
    queryFn: ({ signal }) => autoservicioApi.outbox(filtros, signal),
    enabled: activo,
    placeholderData: keepPreviousData,
  });
}
